import { forwardRef } from 'react'
import DatePicker from 'react-datepicker'
import { ru } from 'date-fns/locale'

import { calculateQuickRange, QuickRangeId } from './calculateQuickRange'
import 'react-datepicker/dist/react-datepicker.css'

export interface DateRangeValue {
  from?: Date
  to?: Date
  mode?: string
}

interface DateRangeFilterProps {
  value: DateRangeValue
  onChange: (v: DateRangeValue) => void
  label?: string

  /**
   * If provided, a radio group is rendered above the date
   * inputs. `value.mode` is updated on click. Consumers decide how to apply the
   * mode (e.g. switch which backend field receives `from`/`to`).
   */
  modes?: { value: string; label: string }[]

  /**
   * Quick-range buttons. Default: ['currentShift', 'prevShift', '24hours', '7days'].
   * Pass `[]` to hide all buttons.
   */
  ranges?: QuickRangeId[]

  withTime?: boolean
}

const DEFAULT_RANGES: QuickRangeId[] = ['currentShift', 'prevShift', '24hours', '7days']

const RANGE_LABELS: Record<QuickRangeId, string> = {
  currentShift: 'Текущая смена',
  prevShift:    'Предыдущая смена',
  '24hours':    '24 часа',
  '7days':      '7 дней',
  '30days':     '30 дней',
  today:        'Сегодня',
  yesterday:    'Вчера',
}

const PickerInput = forwardRef<HTMLInputElement, any>((props, ref) => (
  <input
    {...props}
    ref={ref}
    className='form-control form-control-solid ps-12'
    autoComplete='off'
    type='text'
  />
))
PickerInput.displayName = 'DateRangeFilterInput'

/**
 * Date-range filter for FilterManager.
 *
 * Mounts under the `'dateRange'` key. Value: `{ from?: Date; to?: Date; mode?: string }`.
 *
 * - Optional `modes` prop renders a radio group above the date inputs;
 *   selected mode is stored in `value.mode`. The consumer page decides how to
 *   route it to the backend (e.g. switch between `createdAt` and `closedAt`).
 * - Optional `ranges` prop configures quick-action buttons (default: 4 buttons).
 *   Pass `[]` to hide all buttons.
 *
 * @example
 * <FilterManager
 *   activeFilters={['dateRange']}
 *   defaultValues={{ dateRange: { mode: 'created' } }}
 *   filterProps={{
 *     dateRange: {
 *       label: 'Период',
 *       modes:  [{value: 'created', label: 'По созданию'}, {value: 'closed', label: 'По закрытию'}],
 *       ranges: ['currentShift', '24hours', '7days'],
 *     },
 *   }}
 *   onApplyFilters={({ dateRange }) => {
 *     const field = dateRange?.mode === 'closed' ? 'closedAt' : 'createdAt'
 *     // ... build query using field+from+to
 *   }}
 * />
 */
export function DateRangeFilter({
  value,
  onChange,
  label,
  modes,
  ranges = DEFAULT_RANGES,
  withTime = true,
}: DateRangeFilterProps) {
  const showButtons = ranges.length > 0

  return (
    <>
      {label && (
        <label className='fs-6 form-label fw-bolder text-dark d-block'>{label}</label>
      )}

      {modes && modes.length > 0 && (
        // Радиокнопки, а не сегментированный btn-group: это выбор одного значения из
        // взаимоисключающих, и во всей панели такой выбор выглядит именно так
        // (см. фильтр отчётов «По дате создания / По дате закрытия»).
        <div className='row mb-3'>
          {modes.map((m) => (
            <div className='col-auto' key={m.value}>
              <div className='form-check form-check-custom form-check-solid'>
                <input
                  id={`date-range-mode-${m.value}`}
                  className='form-check-input'
                  type='radio'
                  name='date-range-mode-radio'
                  value={m.value}
                  checked={value.mode === m.value}
                  onChange={() => onChange({ ...value, mode: m.value })}
                />
                <label className='form-check-label' htmlFor={`date-range-mode-${m.value}`}>
                  {m.label}
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className='row g-3'>
        <div className='col-12 col-md-6 col-lg-4 col-xl-3'>
          <label className='fs-6 form-label fw-bolder text-dark d-block'>Дата с</label>
          <div className='position-relative d-flex align-items-center'>
            <div className='symbol symbol-20px me-4 position-absolute ms-4 calendar-icon-datepicker'>
              <i className='fa fa-calendar-alt'></i>
            </div>
            <DatePicker
              selected={value.from ?? null}
              onChange={(d) => onChange({ ...value, from: (d as Date | null) ?? undefined })}
              customInput={<PickerInput placeholder='Дата' />}
              locale={ru}
              showTimeInput={withTime}
              dateFormat={withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
            />
          </div>
        </div>
        <div className='col-12 col-md-6 col-lg-4 col-xl-3'>
          <label className='fs-6 form-label fw-bolder text-dark d-block'>Дата по</label>
          <div className='position-relative d-flex align-items-center'>
            <div className='symbol symbol-20px me-4 position-absolute ms-4 calendar-icon-datepicker'>
              <i className='fa fa-calendar-alt'></i>
            </div>
            <DatePicker
              selected={value.to ?? null}
              onChange={(d) => onChange({ ...value, to: (d as Date | null) ?? undefined })}
              customInput={<PickerInput placeholder='Дата' />}
              locale={ru}
              showTimeInput={withTime}
              dateFormat={withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'}
            />
          </div>
        </div>
      </div>

      {showButtons && (
        <div className='mt-2 timeGroupButton'>
          {ranges.map((id) => (
            <button
              key={id}
              type='button'
              className='btn py-1 px-2 me-2 mb-2 btn-sm btn-secondary'
              onClick={() => {
                const r = calculateQuickRange(id)
                onChange({ ...value, from: r.from, to: r.to })
              }}
            >
              {RANGE_LABELS[id]}
            </button>
          ))}
        </div>
      )}
    </>
  )
}
